import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { validateEmail } from '../utils/inputValidators';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useOrganizations } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastContext';
import {
  EmptyState,
  FormDialog,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
  TableSkeleton,
} from '../components/ui';

interface GroupStore {
  organizationId: string;
  name: string;
  status: 'invited' | 'active' | 'left';
  locationCount: number;
  integrationCount: number;
  acceptedPricingSheetCount: number;
}

interface GroupOverview {
  group: { id: string; name: string; kind: string; parentOrganizationId: string };
  stores: GroupStore[];
}

const STATUS_TONE: Record<GroupStore['status'], 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  invited: 'warning',
  left: 'neutral',
};

/**
 * One predicate decides the mode for the helper text, the error and the request
 * body: an identifier with an @ is an email, anything else an organization ID.
 */
const looksLikeEmail = (value: string): boolean => value.trim().includes('@');

/**
 * The group page: a buying group's or head office's member stores, what each
 * one has, and a way into it.
 *
 * It lives in the store shell — there is no group shell — and is reachable
 * only for someone who manages a group. Switching into a store uses the same
 * organization switcher every other page uses, so from the next request on the
 * shell simply is that store. Someone who manages more than one group picks
 * which one's overview to see; `groups` from the session is the source of
 * truth for both the picker and the "no group" empty state.
 */
export const GroupOverviewPage: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { groups, organizations, switchOrganization, actingOrganizationId } = useOrganizations();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(groups[0]?.id ?? null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteIdentifier, setInviteIdentifier] = useState('');

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    if (!selectedGroupId || !groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
    // Only re-derive the default when the set of manageable groups changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const groupId = selectedGroupId;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['group-overview', groupId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/group/${groupId}/overview`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || 'Could not load the group');
      return body.data as GroupOverview;
    },
    enabled: Boolean(groupId),
    staleTime: 30_000,
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      if (!groupId) throw new Error('No group selected');
      const identifier = inviteIdentifier.trim();
      const payload = looksLikeEmail(identifier)
        ? { email: identifier }
        : { organizationId: identifier };
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/group/${groupId}/invitations`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || 'Could not send the invitation');
      }
      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-overview', groupId] });
      setInviteOpen(false);
      setInviteIdentifier('');
      showToast(t('store.group.invite.success'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const stores = data?.stores ?? [];
  const currentGroup = groups.find((g) => g.id === groupId) ?? null;
  // Invite requires owner/admin of the parent org; managers can open overview but not invite.
  const parentMembership = organizations.find(
    (org) => org.id === data?.group.parentOrganizationId && org.via !== 'group',
  );
  const canInvite =
    parentMembership?.role === 'owner' || parentMembership?.role === 'admin';

  return (
    <PageContainer>
      <PageHeader
        title={data?.group.name || currentGroup?.name || t('store.group.title')}
        description={t('store.group.description')}
        actions={
          groupId && canInvite ? (
            <Button variant="contained" startIcon={<Add />} onClick={() => setInviteOpen(true)}>
              {t('store.group.invite.cta')}
            </Button>
          ) : undefined
        }
      />

      {groups.length > 1 && (
        <Box sx={{ mb: 3, maxWidth: 320 }}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('store.group.picker.label')}
            helperText={t('store.group.picker.helper')}
            value={groupId ?? ''}
            onChange={(e) => setSelectedGroupId(e.target.value)}
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <SectionCard
        dense
        title={t('store.group.storesTitle')}
        subtitle={t('store.group.storesSubtitle', { count: stores.filter((s) => s.status === 'active').length })}
      >
        {isLoading && <TableSkeleton rows={3} columns={5} />}
        {!isLoading && (isError || !groupId) && (
          <Box sx={{ p: 3 }}>
            <EmptyState title={t('store.group.emptyTitle')} description={t('store.group.emptyDescription')} />
          </Box>
        )}
        {!isLoading && !isError && groupId && stores.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState title={t('store.group.noStoresTitle')} description={t('store.group.noStoresDescription')} />
          </Box>
        )}
        {!isLoading && !isError && stores.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('store.group.columns.store')}</TableCell>
                  <TableCell align="right">{t('store.group.columns.locations')}</TableCell>
                  <TableCell align="right">{t('store.group.columns.integrations')}</TableCell>
                  <TableCell align="right">{t('store.group.columns.sheets')}</TableCell>
                  <TableCell align="right">{t('store.group.columns.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stores.map((store) => (
                  <TableRow key={store.organizationId} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {store.name}
                        </Typography>
                        <StatusChip
                          label={t(`store.group.status.${store.status}`)}
                          tone={STATUS_TONE[store.status]}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{store.locationCount}</TableCell>
                    <TableCell align="right">{store.integrationCount}</TableCell>
                    <TableCell align="right">{store.acceptedPricingSheetCount}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={store.status !== 'active' || store.organizationId === actingOrganizationId}
                        onClick={() => switchOrganization(store.organizationId)}
                      >
                        {t('store.group.switchTo')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <FormDialog
        open={inviteOpen}
        title={t('store.group.invite.title', { group: currentGroup?.name || data?.group.name || '' })}
        onClose={() => {
          setInviteOpen(false);
          setInviteIdentifier('');
        }}
        onSubmit={() => inviteMutation.mutate()}
        submitLabel={t('store.group.invite.submit')}
        submitDisabled={
          !inviteIdentifier.trim() ||
          (looksLikeEmail(inviteIdentifier) && validateEmail(inviteIdentifier) !== null) ||
          inviteMutation.isPending
        }
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('store.group.invite.body')}
          </Typography>
          <ValidatedTextField
            label={t('store.group.invite.identifierLabel')}
            value={inviteIdentifier}
            onChange={(e) => setInviteIdentifier(e.target.value)}
            validate={(v) => (looksLikeEmail(v) ? validateEmail(v) : null)}
            helperText={
              !inviteIdentifier.trim()
                ? undefined
                : looksLikeEmail(inviteIdentifier)
                  ? validateEmail(inviteIdentifier) === null
                    ? t('store.group.invite.willInviteEmail')
                    : undefined
                  : t('store.group.invite.willInviteOrg')
            }
            fullWidth
            autoFocus
          />
        </Stack>
      </FormDialog>
    </PageContainer>
  );
};

export default GroupOverviewPage;
